<template>
  <FormGroup
    :helpText="helpText"
    :id="id"
    :invalid-feedback="invalidFeedback ?? errorMessage"
    :label="label"
    :required="required"
    :validFeedback="validFeedback"
  >
    <VueSelect
      class="form-control"
      :class="{ 'is-invalid': meta.validated && !meta.valid }"
      :filterable="filterable"
      :label="labelKey"
      :loading="loading"
      :options="options"
      :placeholder="placeholder"
      :modelValue="value"
      :reduce="getValue"
      @close="onClose()"
      @update:modelValue="onSelect"
      @open="onOpen()"
      @search="onSearch"
      @search:blur="handleBlur"
    >
      <template #search="{ attributes, events }: any">
        <input
          v-bind="attributes"
          v-on="events"
          class="vs__search"
          autocomplete="off"
          :required="!value && required"
        />
      </template>

      <template #option="option: Option">
        <!-- Option slot -->
        <slot name="option" v-bind="{ option }"></slot>
      </template>

      <template #no-options>
        <span v-if="!query.length">Start typing</span>
        <span v-else-if="loading">Loading...</span>
        <span v-else-if="!options?.length">No results found</span>
      </template>

      <template #spinner>
        <div
          v-if="loading"
          style="border-left-color: rgba(88, 151, 251, 0.71)"
          class="vs__spinner"
        ></div>
      </template>

      <template #open-indicator>
        <svg
          width="12"
          height="8"
          viewBox="0 0 12 8"
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
        >
          <path d="M1.41 1L6 5.58 10.59 1 12 2.41 6 8.41 0 2.41 1.41 1z" />
        </svg>
      </template>

      <template #list-footer>
        <li ref="loadEl"></li>
        <li v-show="fetching" class="text-center p-2">
          <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
        </li>

        <!-- list-footer slot -->
        <slot name="list-footer"></slot>
      </template>
    </VueSelect>
  </FormGroup>
</template>

<script lang="ts">
export type Option = string | Record<string, unknown>;

export type AutocompleteFieldProps = FormGroupProps & {
  filterable?: boolean;
  /** Whether the results are paginated and there is a next page. */
  hasNextPage?: boolean;
  /** Label property of the options. Defaults to `label`. */
  labelKey?: string;
  /** Whether more options are being fetched. */
  fetching?: boolean;
  /** Whether the initial options are loading. */
  loading?: boolean;
  /** The selected option. */
  modelValue?: string | null;
  /** List of available options. */
  options?: Option[];
  name?: string;
  placeholder?: string;
  /** Additional validation rules. */
  rules?: StringSchema;
  /** Value property of the options. Defaults to `value`. */
  valueKey?: string;
};

export type AutocompleteFieldEvents = {
  /** Emitted when the list of options closes. */
  (event: "close"): unknown;
  /** Emitted when the list of options opens. */
  (event: "open"): unknown;
  /** Emitted when the user scrolls to the end of the dropdown. */
  (event: "scrollEnd"): unknown;
  /** Emitted when the user selects an option. */
  (event: "update:modelValue", value: string | null): unknown;
  /** Emitted when the user types in the search bar. */
  (event: "update:query", query: string): unknown;
};
</script>

<script lang="ts" setup>
import { uniqueId } from "underscore";
import { useField } from "vee-validate";
import { computed, nextTick, onUnmounted, ref, warn, watch } from "vue";
import { useI18n } from "vue-i18n";
import VueSelect from "vue-select";
import { StringSchema, string } from "yup";
import FormGroup, { FormGroupProps } from "./FormGroup.vue";

// IntersectionObserver for the infinite scroll functionality.
const observer = new IntersectionObserver(onIntersectionObserved);
// Hold the scrolling position.
let scrollTop = 0;
let isDropdownVisible = false;
let listEl: HTMLElement | null = null;

const { t } = useI18n();

const props = withDefaults(defineProps<AutocompleteFieldProps>(), {
  id: uniqueId("field-"),
  labelKey: "label",
  valueKey: "value",
});
const emit = defineEmits<AutocompleteFieldEvents>();

const loadEl = ref<HTMLElement>();
const query = ref<string>("");

const rules = computed<StringSchema<string | null | undefined>>(() => {
  let schema = string().nullable();

  if (props.rules) {
    schema = schema.concat(props.rules);
  }
  if (props.required) {
    schema = schema.required(t("form.field.autocomplete.invalid.required"));
  }

  return schema;
});

const { value, errorMessage, meta, handleBlur } = useField<
  string | null | undefined
>(() => props.name ?? props.id, rules, { syncVModel: true });

onUnmounted(() => {
  listEl = null;
  value.value = null;
  observer.disconnect();
});

// Reload observer.
watch(
  () => props.options,
  async () => {
    let restoreScroll = isDropdownVisible;

    if (loadEl.value) {
      observer.unobserve(loadEl.value);
    }

    await nextTick();

    // Restore scroll position.
    if (restoreScroll && listEl && scrollTop) {
      listEl.scrollTop = scrollTop;
    }

    if (loadEl.value) {
      observer.observe(loadEl.value);
    }
  }
);

/**
 * Callback for VueSelect `open` event.
 *
 * Connect the IntersactionObserver to detect when to fetch next page.
 */
async function onOpen() {
  isDropdownVisible = true;

  emit("open");

  await nextTick();

  if (loadEl.value) {
    observer.observe(loadEl.value);
  }
}

/**
 * Callback for VueSelect `close` event.
 *
 * Disconnect the IntersactionObserver.
 */
function onClose() {
  isDropdownVisible = false;
  scrollTop = 0;

  observer.disconnect();
  emit("close");
}

/**
 * Callback for VueSelect `input` event.
 *
 * @param option
 */
function onSelect(newValue: string | null) {
  value.value = newValue;
}

/**
 * Callback for VueSelect `search` event.
 *
 * @param query - Current search query.
 */
function onSearch(query: string) {
  emit("update:query", query);
}

/**
 * Callback for the IntersectionOvserver.
 */
async function onIntersectionObserved(entries: IntersectionObserverEntry[]) {
  if (!entries[0]?.isIntersecting) {
    return;
  }

  const target = entries[0].target as HTMLElement;
  listEl = <HTMLElement | null>target.offsetParent;
  scrollTop = listEl?.scrollTop ?? 0;

  emit("scrollEnd");
}
/**
 * Extract the value from an {@link Option}.
 *
 * @param option
 */
function getValue(option?: Option | null): string | null {
  if (typeof option === "string" || option instanceof String) {
    return String(option);
  }
  if (option && typeof option === "object" && option[props.valueKey]) {
    return String(option[props.valueKey]);
  }
  if (option) {
    warn(
      `Property "${props.valueKey}" is missing from given option "${option}".`
    );
  }
  return null;
}
</script>
